import React, { useEffect, useLayoutEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import {
  customerService,
  normalizeCustomerLocalization,
  type Customer,
} from "../../../../services/customerService";
import type { DashboardSeverityTodayCounts } from "../../../../services/dashboardOpenSearchService";
import { useDashboardOverviewPanels } from "../hooks/useDashboardOverviewPanels";
import DashboardSeverityTimelineChart from "./DashboardSeverityTimelineChart";
import DashboardSourceSystemGauges from "./DashboardSourceSystemGauges";
import DashboardIpFlowChart from "./DashboardIpFlowChart";
import type { IpFlowMatrix } from "../../../../lib/network/ipFlowMatrix";
import { useDisplayLocalization } from "../../../../lib/datetime/DisplayLocalizationProvider";
import { useTranslation } from "react-i18next";
import {
  formatDateTime,
  formatTimeZoneLabel,
  getDisplayCalendarDayWindow,
  resolveDisplayLocalization,
  setDisplayLocalization,
  type DisplayLocalization,
} from "../../../../lib/datetime/displayDateTime";
import { useQuery } from "@tanstack/react-query";

function useSeverityPalette() {
  const theme = useTheme();
  const { t } = useTranslation();
  return [
    {
      key: "low" as const,
      label: t("dashboard.low"),
      color: theme.palette.success.main,
    },
    {
      key: "medium" as const,
      label: t("dashboard.medium"),
      color: theme.palette.info.main,
    },
    {
      key: "high" as const,
      label: t("dashboard.high"),
      color: theme.palette.warning.main,
    },
    {
      key: "critical" as const,
      label: t("dashboard.critical"),
      color: theme.palette.error.main,
    },
  ];
}

type SeverityPaletteItem = ReturnType<typeof useSeverityPalette>[number];

const EMPTY_SEVERITY_COUNTS: DashboardSeverityTodayCounts = {
  low: 0,
  medium: 0,
  high: 0,
  critical: 0,
};

function formatSignedDelta(delta: number): string {
  if (delta > 0) return `+${delta}`;
  return String(delta);
}

function sumSeverityCounts(values: DashboardSeverityTodayCounts): number {
  return values.low + values.medium + values.high + values.critical;
}

function CompareCell({
  children,
  align = "right",
  muted = false,
}: {
  children: React.ReactNode;
  align?: "left" | "right";
  muted?: boolean;
}) {
  return (
    <Typography
      component="div"
      sx={{
        fontVariantNumeric: "tabular-nums",
        fontWeight: muted ? 600 : 800,
        fontSize: muted ? "0.9375rem" : "1.05rem",
        lineHeight: 1.2,
        color: muted ? "text.secondary" : "text.primary",
        textAlign: align,
        minWidth: 0,
      }}
    >
      {children}
    </Typography>
  );
}

function DeltaValue({ value }: { value: number }) {
  const theme = useTheme();
  const color =
    value > 0
      ? theme.palette.error.main
      : value < 0
        ? theme.palette.success.main
        : theme.palette.text.secondary;

  return (
    <Typography
      component="div"
      sx={{
        fontVariantNumeric: "tabular-nums",
        fontWeight: 800,
        fontSize: "1.05rem",
        lineHeight: 1.2,
        color,
        textAlign: "right",
      }}
    >
      {formatSignedDelta(value)}
    </Typography>
  );
}

function MetricHeaderCell({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      component="div"
      sx={{
        fontSize: "0.6875rem",
        fontWeight: 700,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: "text.secondary",
        textAlign: "right",
        lineHeight: 1.3,
      }}
    >
      {children}
    </Typography>
  );
}

function LiveClockStrip({
  localization,
}: {
  localization: DisplayLocalization;
}) {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const timerId = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(timerId);
  }, []);

  const clockDate = formatDateTime(now, "clockDate", localization);
  const clockTime = formatDateTime(now, "timeSeconds", localization);
  const zoneOffset = formatTimeZoneLabel(
    localization.timeZone,
    localization.locale,
    now,
    "short",
  );

  return (
    <Stack alignItems="flex-end" spacing={0.15} sx={{ minWidth: 0 }}>
      <Typography
        component="time"
        dateTime={now.toISOString()}
        sx={{
          fontVariantNumeric: "tabular-nums",
          fontWeight: 800,
          fontSize: { xs: "1.35rem", md: "1.5rem" },
          lineHeight: 1.1,
          letterSpacing: "0.02em",
        }}
      >
        {clockTime}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        noWrap
        sx={{ maxWidth: "100%" }}
      >
        {clockDate}
        {zoneOffset ? ` · ${zoneOffset}` : ` · ${localization.timeZone}`}
      </Typography>
    </Stack>
  );
}

function useCustomerDisplayLocalization(customer: Customer): {
  localization: DisplayLocalization;
  ready: boolean;
} {
  const display = useDisplayLocalization();
  const { data: hydrated, isFetched } = useQuery({
    queryKey: ["customer-localization", customer.id],
    queryFn: async () => {
      const full = await customerService.getCustomer(String(customer.id));
      return normalizeCustomerLocalization(full);
    },
    enabled: Boolean(customer.id),
    staleTime: 0,
  });

  const localization = useMemo(
    () => resolveDisplayLocalization(hydrated ?? customer, display),
    [hydrated, customer, display],
  );

  useLayoutEffect(() => {
    setDisplayLocalization(localization);
  }, [localization]);

  return {
    localization,
    ready:
      display.ready &&
      (!customer.id || isFetched || Boolean(customer.timeZone?.trim())),
  };
}

function SeverityComparePanel({
  palette,
  todayValues,
  weekAgoValues,
  todayWindowLabel,
  weekAgoWindowLabel,
  localization,
}: {
  palette: SeverityPaletteItem[];
  todayValues: DashboardSeverityTodayCounts;
  weekAgoValues: DashboardSeverityTodayCounts;
  todayWindowLabel: string;
  weekAgoWindowLabel: string;
  localization: DisplayLocalization;
}) {
  const { t } = useTranslation();
  const todayTotal = sumSeverityCounts(todayValues);
  const weekTotal = sumSeverityCounts(weekAgoValues);
  const columns = "minmax(96px, 1.35fr) repeat(3, minmax(56px, 1fr))";

  return (
    <Box
      sx={{
        minWidth: 0,
        height: "100%",
        minHeight: { xs: 420, lg: 480 },
        display: "flex",
        flexDirection: "column",
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        bgcolor: "background.paper",
        p: { xs: 1.5, md: 2 },
        gap: 1.5,
      }}
    >
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
        <Box sx={{ minWidth: 0, pt: 0.25 }}>
          <Typography variant="subtitle2" fontWeight={800}>
            {t("dashboard.severitySnapshot")}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.4 }}>
            {t("dashboard.changeVsLastWeek")}
          </Typography>
        </Box>
        <LiveClockStrip localization={localization} />
      </Stack>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "grid",
          gridTemplateRows: "auto repeat(5, minmax(44px, 1fr))",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: columns,
            columnGap: 1,
            alignItems: "center",
            px: 0.5,
            pb: 0.75,
          }}
        >
          <Box />
          <MetricHeaderCell>{t("dashboard.today")}</MetricHeaderCell>
          <MetricHeaderCell>{t("dashboard.severityLastWeek")}</MetricHeaderCell>
          <MetricHeaderCell>{t("dashboard.severityChange")}</MetricHeaderCell>
        </Box>

        {palette.map((item) => {
          const today = todayValues[item.key];
          const lastWeek = weekAgoValues[item.key];
          return (
            <Box
              key={item.key}
              sx={{
                gridColumn: "1 / -1",
                display: "grid",
                gridTemplateColumns: columns,
                columnGap: 1,
                alignItems: "center",
                height: "100%",
                px: 0.5,
                borderTop: 1,
                borderColor: "divider",
                "&:hover": { bgcolor: "action.hover" },
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: item.color,
                    flexShrink: 0,
                  }}
                />
                <Typography
                  noWrap
                  sx={{ fontWeight: 700, fontSize: "0.875rem", lineHeight: 1.2 }}
                >
                  {item.label}
                </Typography>
              </Stack>
              <CompareCell>{today}</CompareCell>
              <CompareCell muted>{lastWeek}</CompareCell>
              <DeltaValue value={today - lastWeek} />
            </Box>
          );
        })}

        <Box
          sx={{
            gridColumn: "1 / -1",
            display: "grid",
            gridTemplateColumns: columns,
            columnGap: 1,
            alignItems: "center",
            height: "100%",
            px: 0.5,
            borderTop: 1,
            borderColor: "divider",
            bgcolor: (theme) =>
              theme.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "grey.50",
          }}
        >
          <Typography
            noWrap
            sx={{ fontWeight: 800, fontSize: "0.875rem", color: "text.secondary" }}
          >
            {t("dashboard.severityTotal")}
          </Typography>
          <CompareCell>{todayTotal}</CompareCell>
          <CompareCell muted>{weekTotal}</CompareCell>
          <DeltaValue value={todayTotal - weekTotal} />
        </Box>
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
        {t("dashboard.severityCompareHint", {
          today: todayWindowLabel,
          lastWeek: weekAgoWindowLabel,
        })}
      </Typography>
    </Box>
  );
}

function SeverityDistributionSection({
  palette,
  todayValues,
  weekAgoValues,
  todayWindowLabel,
  weekAgoWindowLabel,
  localization,
  ipFlowMatrix,
  ipFlowLoading,
}: {
  palette: SeverityPaletteItem[];
  todayValues: DashboardSeverityTodayCounts;
  weekAgoValues: DashboardSeverityTodayCounts;
  todayWindowLabel: string;
  weekAgoWindowLabel: string;
  localization: DisplayLocalization;
  ipFlowMatrix?: IpFlowMatrix;
  ipFlowLoading?: boolean;
}) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          md: "minmax(300px, 0.88fr) minmax(0, 1.55fr)",
          lg: "minmax(360px, 0.84fr) minmax(0, 1.7fr)",
        },
        gap: { xs: 1.25, md: 1.5 },
        alignItems: "stretch",
      }}
    >
      <SeverityComparePanel
        palette={palette}
        todayValues={todayValues}
        weekAgoValues={weekAgoValues}
        todayWindowLabel={todayWindowLabel}
        weekAgoWindowLabel={weekAgoWindowLabel}
        localization={localization}
      />
      <DashboardIpFlowChart matrix={ipFlowMatrix} loading={ipFlowLoading} />
    </Box>
  );
}

function useLocalCalendarDayWindowLabel(
  dayOffset = 0,
  localization: DisplayLocalization,
) {
  return useMemo(() => {
    const range = getDisplayCalendarDayWindow(dayOffset, localization.timeZone);
    if (dayOffset === 0) {
      return `${formatDateTime(range.start, "time", localization)}–${formatDateTime(range.end, "time", localization)}`;
    }
    return formatDateTime(range.start, "date", localization);
  }, [dayOffset, localization]);
}

export interface DashboardCustomerOverviewPanelProps {
  customer: Customer;
  enabled?: boolean;
  embedded?: boolean;
}

const DashboardCustomerOverviewPanel: React.FC<
  DashboardCustomerOverviewPanelProps
> = ({ customer, enabled = true, embedded = false }) => {
  const severityPalette = useSeverityPalette();
  const { localization, ready: localizationReady } =
    useCustomerDisplayLocalization(customer);
  const calendarDayWindowLabel = useLocalCalendarDayWindowLabel(
    0,
    localization,
  );
  const weekAgoWindowLabel = useLocalCalendarDayWindowLabel(-7, localization);
  const customerId = customer.id ?? null;
  const {
    sourceSystemRows,
    severityToday,
    severityWeekAgo,
    severityTimeline,
    severityTimelineLoading,
    severityTimelineError,
    ipFlowMatrix,
    ipFlowLoading,
    loading,
    error,
  } = useDashboardOverviewPanels(customerId, {
    enabled: enabled && Boolean(customerId) && localizationReady,
    timeZone: localization.timeZone,
  });

  if (!customerId) {
    return null;
  }

  if (loading || !localizationReady) {
    return (
      <Box sx={{ py: 4, px: embedded ? 2 : 0 }}>
        <Stack alignItems="center" spacing={1}>
          <CircularProgress size={28} />
          <Typography variant="body2" color="text.secondary">
            Loading {customer.name ?? "customer"} overview…
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: embedded ? 2 : 0 }}>
        Failed to load overview for {customer.name ?? "customer"}: {error}
      </Alert>
    );
  }

  const severityValues = severityToday ?? EMPTY_SEVERITY_COUNTS;
  const severityWeekAgoValues = severityWeekAgo ?? EMPTY_SEVERITY_COUNTS;

  return (
    <Stack spacing={2} sx={{ p: embedded ? 2 : 0 }}>
      <SeverityDistributionSection
        palette={severityPalette}
        todayValues={severityValues}
        weekAgoValues={severityWeekAgoValues}
        todayWindowLabel={calendarDayWindowLabel}
        weekAgoWindowLabel={weekAgoWindowLabel}
        localization={localization}
        ipFlowMatrix={ipFlowMatrix}
        ipFlowLoading={ipFlowLoading}
      />

      <DashboardSeverityTimelineChart
        timeline={severityTimeline}
        loading={severityTimelineLoading}
        error={severityTimelineError}
        localization={localization}
        windowLabel={calendarDayWindowLabel}
      />

      <DashboardSourceSystemGauges rows={sourceSystemRows} />
    </Stack>
  );
};

export default DashboardCustomerOverviewPanel;
